import React, { useEffect, useState } from "react";
import { Input, Datepicker, Flex } from "@fluentui/react-northstar";
import { useApp } from "../../contexts/appContext";
import { getTimeFromDate, setTimeInDate, isValidTime, isValidDate } from "../../common/dateOperations";
import styles from "./datepickerWithLabel.module.scss";

export interface IDatepickerWithLabelProps {
    label: string;
    date?: Date;
    onChange?: (date: Date) => void;
    disabled?: boolean;
    hideTime?: boolean;
    selectTextOnClick?: boolean;
    allowManualInput?: boolean;
}

const DatepickerWithLabel: React.FunctionComponent<IDatepickerWithLabelProps> = ({
    label, date, onChange, disabled, hideTime, selectTextOnClick, allowManualInput = true
}) => {
    let inputRef: React.RefObject<HTMLInputElement> = React.createRef();

    const [time, setTime] = useState(getTimeFromDate(date));
    const { state: appState } = useApp();
    const { isDarkTheme } = appState;

    useEffect(() => {
        setTime(getTimeFromDate(date));
    }, [date]);

    const dateFormatter = (value?: Date) => {
        if (!value) { return ""; }
        const day = value.getDate();
        const month = value.getMonth() + 1;
        const year = value.getFullYear();
        return `${day}. ${month}. ${year}`;
    };

    const dateChanged = (_: any, data: any) => {
        // Manually typed text can be parsed into an invalid date, do not propagate it
        if (!isValidDate(data.value)) { return; }

        if (onChange) {
            onChange(setTimeInDate(data.value, time));
        }
    };

    const timeChanged = (_: any, data: any) => {
        setTime(data.value);
        if (data.value && isValidTime(data.value) && onChange && date) {
            onChange(setTimeInDate(date, data.value));
        }
    };

    const theme = isDarkTheme ? styles.dark : styles.light;

    return (
        <Flex column gap="gap.smaller">
            <small>{label}</small>
            <Flex gap="gap.smaller">
                {disabled ?
                    <Input value={dateFormatter(date)} disabled className={`${styles.dateInput} ${theme}`} />
                    :
                    <Datepicker
                        selectedDate={date}
                        onDateChange={dateChanged}
                        formatMonthDayYear={dateFormatter}
                        inputOnly
                        className={`${styles.dateInput} ${theme}`}
                        allowManualInput={allowManualInput}
                    />
                }

                {!hideTime &&
                    <Input
                        value={time}
                        className={`${styles.timeInput} ${theme}`}
                        disabled={disabled}
                        onChange={timeChanged}
                        onClick={selectTextOnClick ? () => inputRef.current?.select() : undefined}
                        ref={inputRef}
                    />
                }
            </Flex>
        </Flex>
    );
};

export default DatepickerWithLabel;
